import { Activity } from "lucide-react";
import type { MessageNode } from "./utils";
import {
  formatStepCount,
  getWorkGroupSectionId,
  isToolCallExecuting,
} from "./utils";
import { TimelineRailItem, type StepNodeState } from "./TimelineRailItem";
import { TimelineStepShell } from "./TimelineStepShell";
import { TimelineStep, type TimelineStepContext } from "./TimelineStep";

interface TimelineWorkGroupStepProps {
  nodes: MessageNode[];
  ctx: TimelineStepContext;
  isFirst: boolean;
  isLast: boolean;
  isNewest: boolean;
}

/**
 * A run of the agent's technical steps folded into one collapsible row:
 * reasoning, tool calls, sub-agent tasks, and routed messages. A settled group
 * reads "Agent worked · N steps", a running one "Agent working…". The group
 * takes a single node on the rail and its members render bare inside it. The
 * live caption stays plain — the turn header above owns the shimmer.
 */
export function TimelineWorkGroupStep({
  nodes,
  ctx,
  isFirst,
  isLast,
  isNewest,
}: TimelineWorkGroupStepProps) {
  const sectionId = getWorkGroupSectionId(nodes);
  const isRunning = nodes.some((node) =>
    isToolCallExecuting(node, ctx.isThreadActive),
  );

  return (
    <TimelineRailItem
      state={getWorkGroupNodeState(isRunning, isNewest)}
      isFirst={isFirst}
      isLast={isLast}
      isThreadActive={ctx.isThreadActive}
    >
      <TimelineStepShell
        icon={Activity}
        label={isRunning ? "Agent working…" : "Agent worked"}
        meta={
          isRunning ? undefined : (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatStepCount(nodes.length)}
            </span>
          )
        }
        isExpanded={ctx.expandedSections.has(sectionId)}
        onToggle={() => ctx.onToggleSection(sectionId)}
        bodyClassName="ml-[26px]"
      >
        <div className="space-y-0.5">
          {nodes.map((node) => (
            <TimelineStep key={node.message.id} node={node} ctx={ctx} />
          ))}
        </div>
      </TimelineStepShell>
    </TimelineRailItem>
  );
}

/**
 * Aggregates the members into the group's single rail node: amber while one
 * runs, otherwise the sibling steps' normal green/gray progression.
 */
function getWorkGroupNodeState(
  isRunning: boolean,
  isNewest: boolean,
): StepNodeState {
  if (isRunning) {
    return "running";
  }
  return isNewest ? "fresh" : "done";
}
